/**
 * Exchange Rate Configuration Card Component
 * Allows users to configure manual exchange rates for USD/HKD and USD/CNY
 */

import { useState, useEffect } from 'react';
import { Card } from './Card';
import { Input } from './Input';
import { Button } from './Button';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@/stores/settingsStore';
import { currencyConverter } from '@/services/currencyConverter';
import { unifiedApiService } from '@/services/unifiedApi';
import type { Asset } from '@/types/api';

export const ExchangeRateConfigCard = () => {
  const { t } = useTranslation();
  const { exchangeRates, setExchangeRate } = useSettingsStore();
  
  const [usdHkdInput, setUsdHkdInput] = useState(
    exchangeRates?.USD_HKD?.toFixed(4) || ''
  );
  const [usdCnyInput, setUsdCnyInput] = useState(
    exchangeRates?.USD_CNY?.toFixed(4) || ''
  );
  const [loading, setLoading] = useState(false);
  const [fetchResult, setFetchResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Update inputs when store changes
  useEffect(() => {
    setUsdHkdInput(exchangeRates?.USD_HKD?.toFixed(4) || '');
    setUsdCnyInput(exchangeRates?.USD_CNY?.toFixed(4) || '');
  }, [exchangeRates]);

  const createForexAsset = (from: string, to: string): Asset => ({
    id: `forex-${from.toLowerCase()}_${to.toLowerCase()}`,
    symbol: `${from}/${to}`,
    name: `${from}/${to}`,
    type: 'forex',
    priceStatus: 'loading',
    displayOrder: 0,
    isActive: true,
  });

  const handleFetchRates = async () => {
    setLoading(true);
    setFetchResult(null);

    try {
      const hkdAsset = createForexAsset('USD', 'HKD');
      const cnyAsset = createForexAsset('USD', 'CNY');

      const [hkdPrice, cnyPrice] = await Promise.all([
        unifiedApiService.getAssetPrice(hkdAsset, { timeout: 10000, context: 'exchange-rate-config' }),
        unifiedApiService.getAssetPrice(cnyAsset, { timeout: 10000, context: 'exchange-rate-config' }),
      ]);

      const hkdValue = hkdPrice.current_price;
      const cnyValue = cnyPrice.current_price;

      if (!Number.isFinite(hkdValue) || hkdValue <= 0 || !Number.isFinite(cnyValue) || cnyValue <= 0) {
        throw new Error(t('settings.exchangeRates.fetchFailed'));
      }

      setExchangeRate('USD_HKD', hkdValue);
      setUsdHkdInput(hkdValue.toFixed(4));
      setExchangeRate('USD_CNY', cnyValue);
      setUsdCnyInput(cnyValue.toFixed(4));

      currencyConverter.clearCache();

      setFetchResult({
        success: true,
        message: t('settings.exchangeRates.fetched'),
      });
    } catch (error: any) {
      setFetchResult({
        success: false,
        message: error.message || t('settings.exchangeRates.fetchFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHkd = () => {
    const value = parseFloat(usdHkdInput);
    if (isNaN(value) || value <= 0) {
      setUsdHkdInput(exchangeRates?.USD_HKD?.toFixed(4) || '');
      return;
    }
    setExchangeRate('USD_HKD', value);
    currencyConverter.clearCache();
  };

  const handleSaveCny = () => {
    const value = parseFloat(usdCnyInput);
    if (isNaN(value) || value <= 0) {
      setUsdCnyInput(exchangeRates?.USD_CNY?.toFixed(4) || '');
      return;
    }
    setExchangeRate('USD_CNY', value);
    currencyConverter.clearCache();
  };

  const handleClearHkd = () => {
    setExchangeRate('USD_HKD', null);
    setUsdHkdInput('');
    currencyConverter.clearCache();
  };

  const handleClearCny = () => {
    setExchangeRate('USD_CNY', null);
    setUsdCnyInput('');
    currencyConverter.clearCache();
  };

  // Get current cached rates for display
  const currentHkdRate = currencyConverter.getCachedRate('USD', 'HKD');
  const currentCnyRate = currencyConverter.getCachedRate('USD', 'CNY');

  return (
    <Card className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('settings.exchangeRates.title')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('settings.exchangeRates.description')}
        </p>
      </div>

      <div className="space-y-4">
        {/* USD/HKD Rate */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings.exchangeRates.usdHkd')}
          </label>
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.0001"
              value={usdHkdInput}
              onChange={(e) => setUsdHkdInput(e.target.value)}
              placeholder={currentHkdRate ? currentHkdRate.toFixed(4) : '7.8000'}
              className="flex-1"
            />
            <Button onClick={handleSaveHkd} variant="primary" size="sm">
              {t('settings.save')}
            </Button>
            {exchangeRates?.USD_HKD && (
              <Button onClick={handleClearHkd} variant="secondary" size="sm">
                {t('settings.clear')}
              </Button>
            )}
          </div>
          {currentHkdRate && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('settings.exchangeRates.current')}: {currentHkdRate.toFixed(4)}
            </p>
          )}
        </div>

        {/* USD/CNY Rate */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings.exchangeRates.usdCny')}
          </label>
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.0001"
              value={usdCnyInput}
              onChange={(e) => setUsdCnyInput(e.target.value)}
              placeholder={currentCnyRate ? currentCnyRate.toFixed(4) : '7.2000'}
              className="flex-1"
            />
            <Button onClick={handleSaveCny} variant="primary" size="sm">
              {t('settings.save')}
            </Button>
            {exchangeRates?.USD_CNY && (
              <Button onClick={handleClearCny} variant="secondary" size="sm">
                {t('settings.clear')}
              </Button>
            )}
          </div>
          {currentCnyRate && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('settings.exchangeRates.current')}: {currentCnyRate.toFixed(4)}
            </p>
          )}
        </div>

        {/* Fetch Real-time Rates Button */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={handleFetchRates}
            variant="primary"
            disabled={loading}
            className="w-full"
          >
            {loading ? t('settings.exchangeRates.fetching') : t('settings.exchangeRates.useRealTime')}
          </Button>
          {fetchResult && (
            <p
              className={`text-xs mt-2 ${
                fetchResult.success
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}
            >
              {fetchResult.message}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};
